import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Alert} from "@mui/material";
import {getDsContext} from "@/utils/dsContext";
import {parseNationalTeam} from "@/utils/scoutingData";
import {ClubScoutingAPI} from "@/api/club_scouting";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import MainActions from "@/components/MainActions";
import ClubCrest from "@/components/ClubCrest";
export default function ClubScoutingPanel(props) {
	useSelector((s) => s.user);
	useSelector((s) => s.token);
	useSelector((s) => s.season?.id);
	return <Panel key={getDsContext() + ":" + props.clubId} {...props} />;
}
function Panel({clubId, clubName, onSaved}) {
	const [data, setData] = useState(null),
		[draft, setDraft] = useState(null),
		[alias, setAlias] = useState(""),
		[loading, setLoading] = useState(true),
		[busy, setBusy] = useState(false),
		[error, setError] = useState(""),
		[conflict, setConflict] = useState(false),
		[message, setMessage] = useState("");
	const setResponse = (value) => {
		setData(value);
		setDraft({
			imageUrl: value.imageUrl || "",
			colors: [value.colors?.[0] || "", value.colors?.[1] || ""],
			isNational: value.isNational,
		});
		setConflict(false);
	};
	useEffect(() => {
		let active = true;
		setLoading(true);
		ClubScoutingAPI.read(clubId)
			.then((value) => {
				if (active) setResponse(value);
			})
			.catch(() => {
				if (active) setError("Metadati club non disponibili.");
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [clubId]);
	const reload = async () => {
		setLoading(true);
		setError("");
		try {
			setResponse(await ClubScoutingAPI.read(clubId));
			setMessage("");
		} catch {
			setError("Impossibile ricaricare i dati.");
		} finally {
			setLoading(false);
		}
	};
	const changed =
		draft &&
		data &&
		(draft.imageUrl !== (data.imageUrl || "") ||
			JSON.stringify(draft.colors.filter(Boolean)) !==
				JSON.stringify(data.colors || []) ||
			draft.isNational !== data.isNational);
	const execute = async (operation, success) => {
		setBusy(true);
		setError("");
		setMessage("");
		try {
			setResponse(await operation());
			setAlias("");
			setMessage(success);
			onSaved?.();
		} catch (e) {
			setError(
				e.response?.data?.error ||
					"Operazione non riuscita. Le modifiche restano disponibili.",
			);
			if (e.response?.status === 409) setConflict(true);
		} finally {
			setBusy(false);
		}
	};
	const typeOptions = [
		{value: "unknown", label: "Non definito"},
		{value: "club", label: "Club"},
		{value: "national", label: "Nazionale"},
	];
	const type =
		draft?.isNational === true
			? "national"
			: draft?.isNational === false
				? "club"
				: "unknown";
	const proposed = parseNationalTeam(clubName);
	return (
		<section className="my-3" aria-label="Metadati scouting club">
			<VLFCard
				title="Stemma, colori e alias"
				openable={false}
				content={
					<>
						{loading && <p>Caricamento metadati club…</p>}
						{error && <Alert severity="error">{error}</Alert>}
						{message && <Alert severity="success">{message}</Alert>}
						{(conflict || (!data && !loading)) && (
							<MainActions
								actions={[
									{
										label: "Ricarica dal server (scarta le modifiche)",
										disabled: busy || loading,
										onClick: reload,
									},
								]}
							/>
						)}
						{!loading && data && (
							<>
								<div className="d-flex align-items-center gap-2 mb-3">
									<ClubCrest
										name={clubName}
										logoUrl={draft.imageUrl}
										colors={draft.colors.filter(Boolean)}
										size={40}
									/>
									<span>
										{data.canWrite
											? "Anteprima dello stemma"
											: "Dati condivisi del club"}
									</span>
								</div>
								<fieldset
									disabled={!data.canWrite || busy || conflict}
									style={{border: 0, padding: 0, minWidth: 0}}
								>
									<div className="row">
										<div className="col-12 col-lg-6">
											<VLFTextField
												label="Indirizzo stemma"
												inputProps={{
													"aria-label": "Indirizzo stemma",
													maxLength: 255,
												}}
												value={draft.imageUrl}
												onChange={(value) =>
													setDraft({...draft, imageUrl: value.target.value})
												}
												helperText="Indirizzo HTTPS o percorso relativo. Lascia vuoto per usare i colori sociali."
											/>
										</div>
										{[0, 1].map((index) => (
											<div className="col-6 col-lg-3" key={index}>
												<VLFTextField
													label={"Colore " + (index + 1)}
													inputProps={{
														"aria-label": "Colore " + (index + 1),
														maxLength: 7,
													}}
													value={draft.colors[index]}
													onChange={(value) =>
														setDraft({
															...draft,
															colors: draft.colors.map((c, i) =>
																i === index ? value.target.value : c,
															),
														})
													}
													placeholder="#RRGGBB"
												/>
											</div>
										))}
										<div className="col-12 col-lg-6">
											<VLFSelectNew
												label="Tipo squadra"
												aria-label="Tipo squadra"
												options={typeOptions}
												value={typeOptions.find((o) => o.value === type)}
												isClearable={false}
												isDisabled={!data.canWrite || busy || conflict}
												onChange={(value) =>
													setDraft({
														...draft,
														isNational:
															value.value === "unknown"
																? null
																: value.value === "national",
													})
												}
											/>
										</div>
									</div>
								</fieldset>
								{proposed && draft.isNational == null && data.canWrite && (
									<p>
										Il nome è compatibile con una nazionale: {proposed.country}
										{proposed.ageGroup ? " " + proposed.ageGroup : ""}. Conferma
										il tipo squadra se corretto.
									</p>
								)}
								{data.canWrite && (
									<MainActions
										actions={[
											{
												label: "Salva metadati",
												disabled: busy || conflict || !changed,
												onClick: () =>
													execute(
														() =>
															ClubScoutingAPI.save(clubId, {
																...draft,
																colors: draft.colors.filter(Boolean),
																version: data.version,
															}),
														"Metadati salvati.",
													),
											},
										]}
									/>
								)}
								<h4 className="mt-3">Alias verificati</h4>
								<p>
									I nomi alternativi collegano gli import a questo club; non
									fondono altre anagrafiche.
								</p>
								{(data.aliases || []).length ? (
									<ul>
										{data.aliases.map((value) => (
											<li key={value} style={{overflowWrap: "anywhere"}}>
												{value}
												{data.canWrite && (
													<MainActions
														actions={[
															{
																label: "Rimuovi alias " + value,
																disabled: busy || conflict || changed,
																onClick: () =>
																	execute(
																		() =>
																			ClubScoutingAPI.removeAlias(
																				clubId,
																				value,
																				data.version,
																			),
																		"Alias rimosso.",
																	),
															},
														]}
													/>
												)}
											</li>
										))}
									</ul>
								) : (
									<p>Nessun alias registrato.</p>
								)}
								{data.canWrite && (
									<>
										<VLFTextField
											label="Nuovo alias"
											inputProps={{"aria-label": "Nuovo alias", maxLength: 255}}
											value={alias}
											disabled={busy || conflict || changed}
											onChange={(event) => setAlias(event.target.value)}
										/>
										<MainActions
											actions={[
												{
													label: "Aggiungi alias",
													disabled:
														busy || conflict || changed || !alias.trim(),
													onClick: () =>
														execute(
															() =>
																ClubScoutingAPI.addAlias(
																	clubId,
																	alias.trim(),
																	data.version,
																),
															"Alias registrato.",
														),
												},
											]}
										/>
										{changed && (
											<p>Salva i metadati prima di modificare gli alias.</p>
										)}
									</>
								)}
							</>
						)}
					</>
				}
			/>
		</section>
	);
}
